
import StandardModal from 'components/general/defaultModals/StandardModal';
import { useState, useMemo, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import ProjectSelect from './Pathway/MainModal/ProjectSelect';
import ProjectDetails from './Pathway/MainModal/ProjectDetails';

const normalizeText = (value) => String(value || '').trim();

function MainModal({ pathway, userPathway, show, handleClose, initialProjectId = '', initialAttemptId = '' }) {
  // console.log('MainModal props:', { userPathway }); // Debug log
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [activeProject, setActiveProject] = useState(null);
  const [restoreProjectId, setRestoreProjectId] = useState('');
  const [viewMode, setViewMode] = useState('projects');
  // const [inputValue, setInputValue] = useState('');
  const { user: authUser } = useSelector(x => x.auth);
  const url = process.env.REACT_APP_URL;

  // Move fetchProjects outside useEffect to make it reusable
  const fetchProjects = async () => {
    if (!authUser || !userPathway) return;

    try {
      const response = await fetch(`${url}/user/${userPathway.user}/userPathway/${userPathway._id}/projects`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authUser.token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch projects: ${response.statusText}`);
      }

      const data = await response.json();

      // Map project data to include all fields required by ProjectDetails.js
      const formattedProjects = data.map(project => {
        let found = null;
        for (let i = 1; i <= 5; i++) {
          const level = pathway[`level${i}`];
          if (level) {
            const allProjects = [
              ...(level.mandatoryProjects || []),
              ...(level.electiveProjects || [])
            ];
            found = allProjects.find(p => p._id === project.projectId);
            if (found) break;
          }
        }
        return {
          value: project.projectId,
          label: found ? found.title : project.projectId, // Use project title if found, otherwise fallback to projectId
          title: found?.title || project.projectId,
          description1: found?.description1 || '',
          description2: found?.description2 || '',
          manual: found?.manual || project?.manual || null,
          green: found?.green || null, // Retrieve green from pathway
          amber: found?.amber || null, // Retrieve amber from pathway
          red: found?.red || null, // Retrieve red from pathway
          total: found?.total || null, // Retrieve total from pathway
          type: project.type || 'unknown',
          pathway: project.pathway || {},
          userId: project.userId || userPathway.user, // Ensure userId is included
          ...project // Include all other project details
        };
      });

      setSelectedProjects(formattedProjects.filter(p => p && p.projectId));
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  useEffect(() => {
    fetchProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authUser, userPathway, url, pathway]);

  const allPathwayProjects = useMemo(() => {
    const levels = [1, 2, 3, 4, 5];
    const allProjects = [];

    levels.forEach(levelNum => {
      const levelKey = `level${levelNum}`;
      const levelData = pathway[levelKey] || {};
      const mandatory = levelData.mandatoryProjects || [];
      const elective = levelData.electiveProjects || [];

      mandatory.forEach((project, idx) => {
        allProjects.push({
          value: project._id,
          projectId: project._id,
          label: project.title,
          title: project.title,
          description1: project.description1 || '',
          description2: project.description2 || '',
          manual: project.manual || null,
          green: project.green || null,
          amber: project.amber || null,
          red: project.red || null,
          total: project.total || null,
          type: 'mandatory',
          level: levelNum,
          pos: idx + 1,
          userId: userPathway?.user,
          isAssigned: false,
        });
      });

      elective.forEach((project, idx) => {
        allProjects.push({
          value: project._id,
          projectId: project._id,
          label: project.title,
          title: project.title,
          description1: project.description1 || '',
          description2: project.description2 || '',
          manual: project.manual || null,
          green: project.green || null,
          amber: project.amber || null,
          red: project.red || null,
          total: project.total || null,
          type: 'elective',
          level: levelNum,
          pos: mandatory.length + idx + 1,
          userId: userPathway?.user,
          isAssigned: false,
        });
      });
    });

    return allProjects;
  }, [pathway, userPathway?.user]);

  const displayProjects = useMemo(() => {
    const assignedMap = new Map(
      selectedProjects
        .filter((project) => project && project.projectId)
        .map((project) => [project.projectId, { ...project, isAssigned: true }])
    );

    return allPathwayProjects.map((project) => {
      const assignedProject = assignedMap.get(project.projectId);
      return assignedProject ? { ...project, ...assignedProject } : project;
    });
  }, [allPathwayProjects, selectedProjects]);

  const projectMetadataMap = useMemo(() => {
    return new Map(
      displayProjects
        .filter((project) => project && project.projectId)
        .map((project) => [project.projectId, project])
    );
  }, [displayProjects]);

  const mergeProjectData = useCallback((project) => {
    if (!project?.projectId) return project;
    const metadata = projectMetadataMap.get(project.projectId) || {};
    return {
      ...metadata,
      ...project,
      title: metadata.title || project.title || project.label || project.projectId,
      label: metadata.label || project.label || project.title || project.projectId,
      description1: metadata.description1 || project.description1 || '',
      description2: metadata.description2 || project.description2 || '',
      manual: metadata.manual || project.manual || null,
      green: metadata.green || project.green || null,
      amber: metadata.amber || project.amber || null,
      red: metadata.red || project.red || null,
      total: metadata.total || project.total || null,
    };
  }, [projectMetadataMap]);

  const handleProjectChange = (updatedProject) => {
    setSelectedProjects((prevProjects) => prevProjects.map((project) => (
      project.projectId === updatedProject.projectId
        ? { ...mergeProjectData(project), ...updatedProject, isAssigned: true }
        : project
    )));
    setActiveProject(mergeProjectData(updatedProject));
    setViewMode('details');
  };

  useEffect(() => {
    if (!show || !initialProjectId) return;
    if (!Array.isArray(displayProjects) || displayProjects.length === 0) return;

    const targetProject = displayProjects.find((project) => String(project?.projectId || '') === String(initialProjectId));
    if (!targetProject) return;

    setRestoreProjectId(String(targetProject?.projectId || ''));
    setActiveProject(mergeProjectData(targetProject));
    setViewMode('details');
  }, [displayProjects, initialProjectId, mergeProjectData, show]);

  useEffect(() => {
    if (show) return;

    setActiveProject(null);
    setViewMode('projects');
    setRestoreProjectId('');
  }, [show]);

  const levels = useMemo(() => {
    return [1, 2, 3, 4, 5]
      .map((levelNum) => {
        const levelKey = `level${levelNum}`;
        const levelData = pathway[levelKey];
        if (!levelData) return null;
        const mandatoryProjects = levelData.mandatoryProjects || [];
        const electiveProjects = levelData.electiveProjects || [];
        if (mandatoryProjects.length === 0 && electiveProjects.length === 0 && !levelData.title) return null;
        return {
          label: levelData.title || `Level ${levelNum}`,
          mandatoryProjects,
          electiveProjects,
        };
      })
      .filter(Boolean);
  }, [pathway]);

  const totalProjectCount = levels.reduce(
    (sum, level) => sum + level.mandatoryProjects.length + level.electiveProjects.length,
    0
  );

  return (
    <StandardModal
      show={show}
      handleClose={handleClose}
      title={pathway.name}
      ModalBodyComponent={(
        <div style={{ minHeight: '70vh' }}>
          {viewMode === 'projects' ? (
            <>
              <div className="pathway-modal-hero mb-4">
                <img src={pathway.logo} alt={pathway.name} className="pathway-modal-logo" />
                <div className="pathway-modal-hero-content">
                  <p className="pathway-modal-hero-title mb-2">Pathway Overview</p>
                  <div className="pathway-modal-badges">
                    <span className="pathway-modal-badge">{levels.length} levels</span>
                    <span className="pathway-modal-badge">{totalProjectCount} projects</span>
                    {pathway?.type && <span className="pathway-modal-badge pathway-modal-badge--type">{pathway.type}</span>}
                  </div>
                  <div className="pathway-modal-hero-divider" />
                  <h6 className="pathway-description-title mb-2">Pathway Description</h6>
                  <p className="pathway-modal-description mb-0">{normalizeText(pathway.description)}</p>
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <ProjectSelect
                  displayProjects={displayProjects}
                  setActiveProject={(project) => {
                    const mergedProject = mergeProjectData(project);
                    setRestoreProjectId(String(mergedProject?.projectId || project?.projectId || ''));
                    setActiveProject(mergedProject);
                    setViewMode('details');
                  }}
                  pathway={pathway}
                  restoreProjectId={restoreProjectId}
                  onRestoreHandled={() => setRestoreProjectId('')}
                />
              </div>
            </>
          ) : (
            <ProjectDetails
              activeProject={activeProject}
              setActiveProject={handleProjectChange}
              onBack={() => {
                setRestoreProjectId(String(activeProject?.projectId || restoreProjectId || ''));
                setActiveProject(null);
                setViewMode('projects');
              }}
              userPathway={userPathway}
              initialAttemptId={initialAttemptId}
            />
          )}
        </div>
      )}
    />
  );
}

export default MainModal;
